import React, { useState, useEffect } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useAuthUser } from "react-auth-kit"
import {
  Typography,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material"
import api from "../../../../utils/api"

interface QuestionData {
  id: number
  description: string
  eventId: any
  instanceId: any
  optionCount: number
  //questionNum: number
}

interface InstanceData {
  questionNum: number
  status: string
}

function DeleteQuestionsPage() {
  const { id } = useParams()
  const navigate = useNavigate()

  const [selectedQuestionId, setSelectedQuestionId] = useState("") // New state to store selected question
  const [questions, setQuestions] = useState([] as QuestionData[])
  const [status, setStatus] = useState("")

  const [isDeleteAllConfirmationOpen, setIsDeleteAllConfirmationOpen] =
    useState(false)
  const [deletingAllInstances, setDeletingAllInstances] =
    useState<boolean>(false)

  useEffect(() => {
    fetchQuestions()
  })

  const auth = useAuthUser()
  const eventId = auth()?.eventId?._path?.segments[1] ?? "null"

  //Testing
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false)
  const handleDelete = () => {
    setIsDeleteConfirmationOpen(true)
  }
  const handleConfirmDelete = async () => {
    setIsDeleteConfirmationOpen(false)

    if (!selectedQuestionId) {
      return
    }

    try {
      await api.delete(`/admin/questions/${selectedQuestionId}`)
      navigate(`/admin/event-admin/instance/${id}`, {
        state: { notification: "Question successfully Deleted!" },
      })
    } catch (error) {
      console.error("Error deleting event:", error)
    }

    try {
      const response = await api.get(`/admin/instances/${id}`)
      const instanceData: InstanceData = response.data
      let updatedQuestionNum = instanceData.questionNum

      updatedQuestionNum = instanceData.questionNum - 1

      await api.put(`/admin/instances/${id}/count`, {
        questionNum: updatedQuestionNum,
      })

      return true
    } catch (error) {
      console.error("Failed to update question num:", error)
      return false
    }
  }
  const handleCancelDelete = () => {
    setIsDeleteConfirmationOpen(false)
  }

  // all instance ids from eventId
  useEffect(() => {
    if (eventId) {
      api
        .get(`/admin/events/${eventId}/voting-instances-ids`)
        .then((response) => {
          const instances = response.data
          const exists = instances.some((instance: any) => instance.id === id)
          if (!exists) {
            navigate(
              "/not-authorized?message=The%20voting%20instance%20you%20are%20trying%20to%20access%20does%20not%20exist."
            )
          }
        })
        .catch((error) => {
          console.error("Error fetching voting instances:", error)
        })

      api
        .get(`/admin/instances/${id}/status`)
        .then((response) => {
          const status = response.data.status
          if (status !== "pre-voting") {
            navigate(
              "/not-authorized?message=The%20voting%20instance%20you%20are%20trying%20to%20access%20is%20not%20open%20for%20voting."
            )
          }
          setStatus(status)
        })
        .catch((error) => {
          console.error("Error fetching voting instances:", error)
        })
    }
  }, [eventId, id, navigate])

  // Fetch questions function
  const fetchQuestions = async () => {
    try {
      const response = await api.get(`/admin/instances/${id}/questions`)

      const questionData = response.data
      setQuestions(questionData)
    } catch (error) {
      console.error("Error fetching questions:", error)
    }
  }

  const handleQuestionSelect = async (questionId: any) => {
    setSelectedQuestionId(questionId)
    // Fetch question data for editing based on the selected questionId
  }

  const handleDeleteAll = async () => {
    setDeletingAllInstances(true)

    for (let question of questions) {
      try {
        await api.delete(`/admin/questions/${question.id}`)
      } catch (error) {
        console.error(`Error deleting instance with ID: ${question.id}:`, error)
      }
    }
    setQuestions((prevQuestions) =>
      prevQuestions.filter(
        (question) => !questions.some((i) => i.id === question.id)
      )
    )
    try {
      const response = await api.get(`/admin/instances/${id}`)
      const instanceData: InstanceData = response.data
      let updatedQuestionNum = instanceData.questionNum

      updatedQuestionNum = instanceData.questionNum * 0

      await api.put(`/admin/instances/${id}/count`, {
        questionNum: updatedQuestionNum,
      })

      return true
    } catch (error) {
      console.error("Failed to update question num:", error)
      return false
    }
  }

  return (
    <Container component="main">
      <Typography variant="h4" gutterBottom>
        Delete Question
      </Typography>
      <form onSubmit={(e) => e.preventDefault()}>
        {/* Dropdown for selecting a question */}
        <FormControl fullWidth margin="normal" required>
          {questions.length > 0 && (
            <InputLabel id="question-select-label">
              Select a Question
            </InputLabel>
          )}
          {questions.length > 0 ? (
            <Select
              label="Select a Question"
              labelId="question-select-label"
              value={selectedQuestionId}
              onChange={(e) => handleQuestionSelect(e.target.value)}
            >
              {status === "pre-voting" ? (
                questions?.map((question) => (
                  <MenuItem key={question.id} value={question.id}>
                    {question.description}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No questions available</MenuItem>
              )}
            </Select>
          ) : (
            <Typography variant="body1">No questions available</Typography>
          )}
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleDelete}
          >
            Delete
          </Button>
        </FormControl>
        {questions.length > 0 && (
          <Button
            variant="contained"
            style={{ backgroundColor: "red" }}
            aria-label="Delete All"
            sx={{ marginRight: 1 }}
            onClick={() => {
              setIsDeleteAllConfirmationOpen(true)
            }}
          >
            Delete All
          </Button>
        )}

        {/*Start of Delete All Dialog */}
        <Dialog
          open={isDeleteAllConfirmationOpen}
          onClose={() => {
            setIsDeleteAllConfirmationOpen(false)
            setDeletingAllInstances(false) // Reset the state when closing the dialog
          }}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            {deletingAllInstances ? (
              <Typography>
                Please wait, this may take a couple of minutes...
              </Typography>
            ) : (
              <Typography>
                Are you sure you want to delete all instances?
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setIsDeleteAllConfirmationOpen(false)
                setDeletingAllInstances(false) // Reset the state when canceling
              }}
              color="primary"
            >
              Cancel
            </Button>
            {!deletingAllInstances && ( // Hide the Confirm button during the deletion process
              <Button
                onClick={async () => {
                  setDeletingAllInstances(true)
                  await handleDeleteAll()
                  setIsDeleteAllConfirmationOpen(false)
                  setDeletingAllInstances(false) // Reset the state after deletion
                }}
                color="primary"
              >
                Confirm
              </Button>
            )}
          </DialogActions>
        </Dialog>

        <Dialog open={isDeleteConfirmationOpen} onClose={handleCancelDelete}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this instance?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDelete} color="primary">
              Cancel
            </Button>
            <Button onClick={handleConfirmDelete} color="primary" autoFocus>
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </form>
    </Container>
  )
}

export default DeleteQuestionsPage
